'use client';

import {
  motion,
  useInView,
  type UseInViewOptions,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import type { ReactNode } from 'react';
import { useRef } from 'react';

import {
  fadeInUp,
  staggerCard,
  staggerContainer,
  staggerContainerFast,
  staggerItem,
} from './animations';

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  variants?: Variants;
  margin?: UseInViewOptions['margin'];
  once?: boolean;
  id?: string;
}

export const ScrollReveal = ({
  children,
  className,
  variants = fadeInUp,
  margin = '-80px',
  once = true,
  id,
}: ScrollRevealProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin });
  const shouldReduceMotion = useReducedMotion();

  // prefers-reduced-motion 지원
  if (shouldReduceMotion) {
    return (
      <div id={id} className={className}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      id={id}
      variants={variants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      className={className}
    >
      {children}
    </motion.div>
  );
};

interface ScrollRevealListProps {
  children: ReactNode;
  className?: string;
  margin?: UseInViewOptions['margin'];
  fast?: boolean;
}

export const ScrollRevealList = ({
  children,
  className,
  margin = '-80px',
  fast = false,
}: ScrollRevealListProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin });
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      variants={fast ? staggerContainerFast : staggerContainer}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      className={className}
    >
      {children}
    </motion.div>
  );
};

interface ScrollRevealItemProps {
  children: ReactNode;
  className?: string;
  asCard?: boolean;
}

export const ScrollRevealItem = ({
  children,
  className,
  asCard = false,
}: ScrollRevealItemProps) => {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      variants={asCard ? staggerCard : staggerItem}
      className={className}
    >
      {children}
    </motion.div>
  );
};
